// src/utils/pdfExporter.js
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * وظيفة لتحميل الخط العربي من جوجل فونتس
 * @returns {Promise<string>} - وعد يحتوي على بيانات الخط بصيغة base64
 */
async function loadAmiriFont() {
  try {
    // الرابط المباشر لخط أميري من جوجل فونتس
    const fontUrl = 'https://fonts.gstatic.com/s/amiri/v24/J7aRnpd8CGxBHqUq.ttf';
    
    // تحميل ملف الخط
    const response = await fetch(fontUrl);
    
    if (!response.ok) {
      throw new Error(`فشل تحميل الخط: ${response.status} ${response.statusText}`);
    }
    
    // تحويل الخط إلى ArrayBuffer
    const fontArrayBuffer = await response.arrayBuffer();
    
    // تحويل ArrayBuffer إلى Base64
    const fontBase64 = arrayBufferToBase64(fontArrayBuffer);
    
    console.log('تم تحميل خط أميري بنجاح');
    return fontBase64;
  } catch (error) {
    console.error('خطأ أثناء تحميل خط أميري:', error);
    return null;
  }
}

/**
 * وظيفة مساعدة لتحويل ArrayBuffer إلى سلسلة Base64
 * @param {ArrayBuffer} buffer - البيانات الثنائية للخط
 * @returns {string} - سلسلة Base64
 */
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  return window.btoa(binary);
}

/**
 * تصدير البيانات إلى ملف PDF مع دعم كامل للغة العربية
 * @param {Object} options - خيارات إضافية للتصدير
 * @param {Object} data - البيانات المراد تصديرها
 */
export async function exportToPDFArabic(options = {}, data = {}) {
  try {
    // خيارات افتراضية
    const defaultOptions = {
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      filename: 'تقرير_الطلبات.pdf'
    };
    
    // دمج الخيارات مع الخيارات الافتراضية
    const pdfOptions = { ...defaultOptions, ...options };
    
    // إنشاء مستند PDF جديد
    const doc = new jsPDF(
      pdfOptions.orientation,
      pdfOptions.unit,
      pdfOptions.format
    );
    
    // تحميل خط أميري
    const amiriFont = await loadAmiriFont();
    
    if (!amiriFont) {
      console.warn('فشل تحميل خط أميري، سيتم استخدام الطريقة البديلة');
      return exportToPDFAlternative(pdfOptions.filename);
    }
    
    // إضافة الخط إلى المستند
    doc.addFileToVFS('Amiri-Regular.ttf', amiriFont);
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
    doc.setFont('Amiri');
    
    // ضبط اتجاه النص من اليمين إلى اليسار
    doc.setR2L(true);
    
    // إضافة عنوان للمستند
    doc.setFontSize(18);
    doc.text('تقرير الطلبات', 140, 20, { align: 'center' });
    
    // إضافة تاريخ التقرير
    const today = new Date();
    const formattedDate = today.toLocaleDateString('ar-EG');
    doc.setFontSize(10);
    doc.text(`تاريخ التقرير: ${formattedDate}`, 20, 30);
    
    // استخدام البيانات المقدمة أو بيانات افتراضية
    const stats = data.stats || {
      totalOrders: 0,
      completedOrders: 0,
      cancelledOrders: 0,
      totalRevenue: 0,
      totalDeliveryFees: 0,
      avgOrderValue: 0
    };
    
    // دالة تنسيق العملة (دينار كويتي)
    const formatCurrency = (amount) => `${amount.toFixed(3)} د.ك`;
    
    // إضافة عنوان الإحصائيات
    doc.setFontSize(14);
    doc.text('الإحصائيات العامة', 280, 40, { align: 'right' });
    
    // تحضير بيانات جدول الإحصائيات
    const statRows = [
      ['إجمالي الطلبات', stats.totalOrders.toString()],
      ['الطلبات المكتملة', stats.completedOrders.toString()],
      ['الطلبات الملغاة', stats.cancelledOrders.toString()],
      ['إجمالي الإيرادات', formatCurrency(stats.totalRevenue)],
      ['إجمالي رسوم التوصيل', formatCurrency(stats.totalDeliveryFees)],
      ['متوسط قيمة الطلب', formatCurrency(stats.avgOrderValue)]
    ];
    
    // إنشاء جدول الإحصائيات
    doc.autoTable({
      startY: 45,
      head: [['المؤشر', 'القيمة']],
      body: statRows,
      theme: 'grid',
      styles: {
        font: 'Amiri',
        fontSize: 10,
        cellPadding: 5,
        halign: 'right'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        font: 'Amiri',
        fontStyle: 'bold'
      },
      margin: { right: 15 }
    });
    
    // الحصول على موضع نهاية الجدول السابق
    const finalY = doc.lastAutoTable.finalY || 45;
    
    // إضافة جدول الطلبات إذا كانت متاحة
    if (data.orders && data.orders.length > 0) {
      // إضافة عنوان جدول الطلبات
      doc.setFontSize(14);
      doc.text('تفاصيل الطلبات', 280, finalY + 10, { align: 'right' });
      
      // تحضير بيانات جدول الطلبات
      const orderRows = data.orders.map(order => [
        order.id,
        order.dateFormatted || order.date,
        order.status || '',
        formatCurrency(order.total || 0),
        order.paymentMethod || ''
      ]);
      
      // إنشاء جدول الطلبات
      doc.autoTable({
        startY: finalY + 15,
        head: [['المعرف', 'التاريخ', 'الحالة', 'المبلغ', 'طريقة الدفع']],
        body: orderRows,
        theme: 'grid',
        styles: {
          font: 'Amiri',
          fontSize: 9,
          cellPadding: 3,
          halign: 'right'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          font: 'Amiri',
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 30 },
          2: { cellWidth: 40 },
          3: { cellWidth: 30 },
          4: { cellWidth: 40 }
        },
        margin: { right: 15 }
      });
    }
    
    // إضافة أرقام الصفحات
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`الصفحة ${i} من ${pageCount}`, 145, 200, { align: 'center' });
    }
    
    // حفظ الملف
    doc.save(pdfOptions.filename);
    
    console.log('تم إنشاء ملف PDF بنجاح');
    return true;
  } catch (error) {
    console.error('خطأ في إنشاء ملف PDF:', error);
    alert('حدث خطأ أثناء إنشاء ملف PDF');
    
    // محاولة استخدام الطريقة البديلة
    try {
      await exportToPDFAlternative(options.filename || 'تقرير_الطلبات.pdf');
      return true;
    } catch (fallbackError) {
      console.error('فشلت الطريقة البديلة أيضًا:', fallbackError);
      return false;
    }
  }
}

/**
 * طريقة بديلة لتصدير PDF باستخدام html2canvas
 * @param {string} filename - اسم الملف
 */
export async function exportToPDFAlternative(filename = 'تقرير_الطلبات.pdf') {
  try {
    // العثور على عنصر الصفحة
    const element = document.getElementById('report-container');
    
    if (!element) {
      throw new Error('لم يتم العثور على عنصر التقرير');
    }
    
    // تحويل الصفحة إلى صورة
    const canvas = await html2canvas(element, {
      scale: 2, // جودة أفضل
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    // تحويل الصورة إلى بيانات PNG
    const imgData = canvas.toDataURL('image/png');
    
    // إنشاء PDF جديد
    const pdf = new jsPDF('l', 'mm', 'a4');
    
    // حساب أبعاد الصورة
    const imgWidth = 280;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // إضافة الصورة إلى PDF
    pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
    
    // إضافة صفحات إضافية إذا كانت الصورة كبيرة
    let heightLeft = imgHeight;
    let position = 0;
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    // حفظ الملف
    pdf.save(filename);
    
    console.log('تم إنشاء ملف PDF باستخدام الطريقة البديلة');
    return true;
  } catch (error) {
    console.error('خطأ في الطريقة البديلة لإنشاء PDF:', error);
    alert('حدث خطأ أثناء إنشاء ملف PDF');
    return false;
  }
}